import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormControlLabel,
  IconButton,
  Grid,
  Chip,
  Stack,
  DialogActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  CircularProgress,
  OutlinedInput,
  InputAdornment,
  Checkbox,
  ListItemText,
  Radio,
  RadioGroup,
} from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import CustomToast from './CustomToast';
import {
  CancelOutlined,
  CheckCircleOutline,
  CloseOutlined,
  LocalShippingOutlined,
  LoopOutlined,
  WarehouseOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { toastr } from 'react-redux-toastr';
import { i18next } from '@/config/i18n';
import TimeField from './TimeField';
import { Vehicles, VEHICLE_ROUTING_TYPES } from '@/config/types/Vehicles';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/index';
import { getTerritories } from '@/actions/territories/thunks';
import { toggleLoading } from '@/actions/common/commonPure';
import { getTerritoryById } from '@/actions/territories/thunks';
import { LoadClass } from '@/config/types/LoadClass';
import { useSettings } from '../Settings/SettingsContext';

const useStyles = makeStyles({
  drawer: { width: '100%', maxWidth: 530 },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 10,
    background: 'rgba(239, 89, 39, 0.12)',
  },
  addTitle: {
    marginLeft: '5px',
    fontSize: '18px'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    marginTop: 6,
    padding: 16
  },
  redAsterisk: {
    color: '#d32f2f',
    '&.Mui-focused': {
      color: '#d32f2f'
    },
  },
  headerTitle: {
    background: '#dfdede',
    padding: '10px'
  },
  addressTitle: {
    background: '#dfdede',
    padding: '10px',
    marginBottom: '5px'
  },
  addressContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16
  },
  warningwrapper: {
    background: '#f59b7d36',
    borderRadius: '50px',
    width: '50px',
    height: '50px'
  },
  warningIcon: {
    borderRadius: '50px',
    fontSize: '31px',
    padding: '4px',
    color: '#ef5927',
    background: 'rgb(239 89 39 / 22%)',
    margin: '5px',
    width: '40px',
    height: '40px'
  },
  warningTitle: {
    fontSize: '17px',
    marginLeft: '10px'
  },
  contentArea: {
    padding: '0px',
    margin: '16px 28px'
  },
  Closebtn: {
    border: '1px solid #e5e5e5',
    color: '#000000',
    padding: '5px 16px'
  },
});

const NOTES_MAX = 255;

type VehicleFormType = Omit<Vehicles, 'id'> & { notes: string | null };

const LOCATION_KEYS = ['start_location', 'end_location'] as const;
type LocationKey = typeof LOCATION_KEYS[number];
type FeatureKey = 'has_liftgate' | 'has_trailer';
type RoundTripKey = 'round_trip';

type ClearableContainerProps = React.PropsWithChildren<{
  onClear: () => void;
  show?: boolean;
  sx?: any;
}>;

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (vehicle: VehicleFormType, id?: string) => Promise<void>;
  initialData?: Vehicles;
  loadClasses: LoadClass[];
  loadingClasses: boolean;
}

interface Feature {
  key: FeatureKey;
  labelKey: string;
  icon: React.ReactNode;
}
interface RoundTripFeature {
  key: RoundTripKey;
  labelKey: string;
  icon: React.ReactNode;
}

type ClearableTFProps = React.ComponentProps<typeof TextField> & {
  onClear?: () => void;
  showClear?: boolean;
};

const defaultForm: VehicleFormType = {
  name: '',
  is_active: false,
  vehicle_routing_type: VEHICLE_ROUTING_TYPES[0],
  fixed_cost_eur: 0,
  variable_cost_eur_per_km: 0,
  capacities: [0, 0, 0],
  travel_time_factor: 1,
  operation_start_window: { start: null, end: null },
  max_working_time_min: null,
  max_allowed_distance_km: null,
  operation_end: null,
  round_trip: null,
  has_liftgate: false,
  has_trailer: false,
  max_adr_points: 0,
  max_load_height_cm: null,
  max_load_length_cm: null,
  max_load_width_cm: null,
  depot_service_time_config: {
    loading_service_time_config: { fixed_service_time_s: 0, variable_service_time_s_per_load: [0, 0, 0] },
    unloading_service_time_config: { fixed_service_time_s: 0, variable_service_time_s_per_load: [0, 0, 0] },
  },
  fixed_service_time_s: 0,
  variable_service_time_s_per_load: [0, 0, 0],
  start_location: {
    address: { country: null, city: '', postal_code: '', street: null, house_number: '' },
    coordinates: null,
  },
  end_location: {
    address: { country: null, city: '', postal_code: '', street: null, house_number: '' },
    coordinates: null,
  },
  road_weight_limit_kg: null,
  width_cm: null,
  height_cm: null,
  length_cm: null,
  load_class_name: null,
  vehicle_type: null,
  primary_territory_names: null,
  secondary_territory_names: null,
  notes: null,
};

/** Clearable TextField (no nesting another TextField inside) */
const ClearableTextField: React.FC<ClearableTFProps> = ({
  onClear,
  showClear,
  InputProps,
  sx,
  ...rest
}) => {
  const shouldShowClear = Boolean(showClear) && !rest.required;
  const isNumber = rest.type === 'number';
  const isMultiline = Boolean(rest.multiline);

  const SPINNER_W = 26;
  const CLEAR_W = 28;
  const GAP = 6;

  if (isNumber || isMultiline) {
    return (
      <Box
        sx={{
          position: 'relative',
          '&:hover ._clearBtn': {
            opacity: shouldShowClear ? 1 : 0,
            pointerEvents: shouldShowClear ? 'auto' : 'none',
          },
          ...sx,
        }}
      >
        <TextField
          {...rest}
          sx={{
            '& .MuiInputBase-inputMultiline': {
              paddingRight: shouldShowClear ? `${CLEAR_W + 8}px` : undefined,
            },
          }}
          InputProps={{
            ...InputProps,
          }}
        />

        {shouldShowClear && (
          <IconButton
            className="_clearBtn"
            size="small"
            onClick={onClear}
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            aria-label="clear"
            sx={{
              position: 'absolute',
              right: isNumber ? SPINNER_W + GAP : 6,
              top: isMultiline ? 6 : '50%',
              transform: isMultiline ? 'none' : 'translateY(-50%)',
              opacity: 0,
              pointerEvents: 'none',
              transition: 'opacity .15s ease',
              zIndex: 2,
            }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  }

  // non-number & non-multiline: normal endAdornment
  return (
    <TextField
      {...rest}
      sx={{
        '& .MuiInputBase-root:hover ._clearBtn': {
          opacity: shouldShowClear ? 1 : 0,
          pointerEvents: shouldShowClear ? 'auto' : 'none',
        },
        ...sx,
      }}
      InputProps={{
        ...InputProps,
        endAdornment: shouldShowClear ? (
          <InputAdornment position="end" sx={{ m: 0 }}>
            <IconButton
              className="_clearBtn"
              edge="end"
              size="small"
              onClick={onClear}
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
              aria-label="clear"
              sx={{ opacity: 0, pointerEvents: 'none', transition: 'opacity .15s ease' }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : (InputProps?.endAdornment ?? null),
      }}
    />
  );
};

/** Clearable Select with OutlinedInput endAdornment */
const ClearableSelect: React.FC<{
  label: string;
  value: any;
  onChange: (e: any) => void;
  onClear?: () => void;
  showClear?: boolean;
  children: React.ReactNode;
  size?: 'small' | 'medium';
  disabled?: boolean;
  sx?: any;
  labelId?: string;
  displayEmpty?: boolean;
}> = ({
  label, value, onChange, onClear, showClear,
  children, size = 'small', disabled, sx, labelId, displayEmpty = true
}) => {
    const ARROW_W = 15

    return (
      <FormControl
        fullWidth
        size={size}
        disabled={disabled}
        sx={{
          position: 'relative',
          '& ._clearBtn': { opacity: 0, pointerEvents: 'none', transition: 'opacity .15s ease' },
          '& .MuiOutlinedInput-root:hover ._clearBtn': {
            opacity: showClear ? 1 : 0,
            pointerEvents: showClear ? 'auto' : 'none',
          },
          '& .MuiSelect-select': {
            paddingRight: showClear ? `${ARROW_W}px` : `${ARROW_W}px`,
          },
          ...sx,
        }}
      >
        <InputLabel id={labelId} shrink>{label}</InputLabel>

        <Select
          labelId={labelId}
          value={value ?? ''}
          label={label}
          onChange={onChange}
          displayEmpty={displayEmpty}
          input={
            <OutlinedInput
              label={label}
              endAdornment={
                showClear ? (
                  <InputAdornment position="end" sx={{ ml: 0, mr: `${ARROW_W}px` }}>
                    <IconButton
                      className="_clearBtn"
                      edge="end"
                      size="small"
                      onClick={onClear}
                      tabIndex={-1}
                      onMouseDown={(e) => e.preventDefault()}
                      aria-label="clear"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
            />
          }
        >
          {children}
        </Select>
      </FormControl>
    );
  };

/** Wrapper for custom inputs (TimeField) to show clear icon without stealing focus */
const ClearableContainer: React.FC<ClearableContainerProps> = ({
  children,
  onClear,
  show = true,
  sx,
}) => {
  return (
    <Box
      sx={{
        position: "relative",
        "&:hover ._clearBtn": {
          opacity: show ? 1 : 0,
          pointerEvents: show ? "auto" : "none",
        },
        ...sx,
      }}
    >
      {children}

      <IconButton
        className="_clearBtn"
        size="small"
        onClick={onClear}
        tabIndex={-1}
        onMouseDown={(e) => e.preventDefault()}
        aria-label="clear"
        sx={{
          position: "absolute",
          right: 32,
          top: 6,
          opacity: 0,
          pointerEvents: "none",
          transition: "opacity .15s ease",
        }}
      >
        <ClearIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

/** CommaSeparatedStringField using ClearableTextField */
const CommaSeparatedStringField: React.FC<{
  label: string;
  values?: string[] | null;
  onChange(v: string[]): void;
  onClear?: () => void;
  showClear?: boolean;
  sx?: any;
}> = React.memo(({ label, values, onChange, onClear, showClear, sx }) => {
  const safeValues = values ?? [];
  const str = safeValues.join(', ');
  const handle = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(
      (e.target.value || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    );
  };
  return (
    <ClearableTextField
      label={label}
      size="small"
      fullWidth
      value={str}
      onChange={handle}
      InputLabelProps={{ shrink: true }}
      onClear={onClear}
      showClear={showClear}
      sx={{
        '& .MuiInputAdornment-root.MuiInputAdornment-positionEnd': {
          marginLeft: 0,
          marginRight: 0,
        }
      }}
    />
  );
});

const VehicleFormDrawer: React.FC<Props> = ({ open, onClose, onSave, initialData, loadClasses, loadingClasses }) => {
  const classes = useStyles();
  const isEdit = Boolean(initialData);
  const [form, setForm] = useState<VehicleFormType>(defaultForm);
  const [savedForm, setSavedForm] = useState<VehicleFormType>(defaultForm);
  const [touched, setTouched] = useState<{ name?: boolean; capacities?: boolean; max_adr_points?: boolean }>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingClose, setPendingClose] = useState<() => void>(() => () => { });
  const hasUnsaved = JSON.stringify(form) !== JSON.stringify(savedForm);
  const [unsavedDismissed, setUnsavedDismissed] = useState(false);
  const [saving, setSaving] = useState(false);
  const { settings } = useSettings();
  const dispatch: AppDispatch = useDispatch();
  const territories = useSelector((state: RootState) => state.territories?.territories ?? []);
  const loading = useSelector((state: RootState) => state.core?.loading ?? false);
  const requestedTerritoryIdsRef = useRef<Set<string>>(new Set());
  const enrichInFlightRef = useRef(false);

  // Track territory radio mode separately from form array values,
  // because [] is ambiguous ("selected but nothing checked" vs "no territories allowed")
  type TerritoryMode = 'all' | 'selected' | 'none';
  const deriveTerritoryMode = (arr: string[] | null | undefined): TerritoryMode => {
    if (arr === null || arr === undefined) return 'all';
    if (arr.length === 0) return 'none';
    return 'selected';
  };
  const [primaryTerritoryMode, setPrimaryTerritoryMode] = useState<TerritoryMode>(() => deriveTerritoryMode(defaultForm.primary_territory_names));
  const [secondaryTerritoryMode, setSecondaryTerritoryMode] = useState<TerritoryMode>(() => deriveTerritoryMode(defaultForm.secondary_territory_names));

  useEffect(() => {
    if (!open) return;

    (async () => {
      dispatch(toggleLoading(true));
      try {
        await dispatch(getTerritories(false, 100) as any);
      } finally {
        dispatch(toggleLoading(false));
      }
    })();
  }, [open, dispatch]);
  const dimensions = settings.load_dimensions || [];
  const vtLabelId = 'vehicle-type-label';
  const lcLabelId = 'load-class-label';
  const [unset, setUnset] = useState<Record<string, boolean>>({});
  const markUnset = (path: string, v: boolean) =>
    setUnset(u => ({ ...u, [path]: v }));
  const setF = <K extends keyof typeof form>(key: K, value: typeof form[K]) => {
    setForm(f => ({ ...f, [key]: value } as any));
    markUnset(String(key), false);
  };
  const stopVar = form.variable_service_time_s_per_load ?? [0, 0, 0];

  const [saveToast, setSaveToast] = useState<{
    open: boolean;
    msg: React.ReactNode;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, msg: '', severity: 'success' });

  const clearField = <K extends keyof typeof form>(key: K, to: typeof form[K]) => {
    setForm(f => ({ ...f, [key]: to } as any));
    markUnset(String(key), true);
  };

  const updateField = <K extends keyof typeof form>(key: K, value: typeof form[K]) =>
    setForm(f => ({ ...f, [key]: value } as any));

  const enrichTerritoriesWithPostalCodes = async (ids: string[]) => {
    const CONCURRENCY = 8;
    for (let i = 0; i < ids.length; i += CONCURRENCY) {
      const chunk = ids.slice(i, i + CONCURRENCY);
      await Promise.all(chunk.map((id) => dispatch(getTerritoryById(id) as any)));
    }
  };
  const updateLocation = (locationKey: LocationKey, path: string, value: any) => {
    setForm(prev => {
      const updatedLocation = {
        ...prev[locationKey],
        address: {
          ...prev[locationKey]?.address ?? {
            country: null,
            city: '',
            postal_code: '',
            street: null,
            house_number: ''
          },
        },
      };
      const keys = path.split('.');
      let target: any = updatedLocation;
      for (let i = 0; i < keys.length - 1; i++) {
        if (target[keys[i]] == null) target[keys[i]] = {};
        target = target[keys[i]];
      }
      target[keys[keys.length - 1]] = value;
      return { ...prev, [locationKey]: updatedLocation };
    });
  };

  const isAddressEmpty = (address: any): boolean => {
    const values = [
      address?.city,
      address?.postal_code,
      address?.country,
      address?.street,
      address?.house_number,
    ];
    return values.every(val => !val || val.trim?.() === '');
  };

  // --- Dirty helpers ---
  const deepEqual = (a: any, b: any): boolean => {
    if (a === b) return true;
    if (a == null && b == null) return true;
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], b[i])) return false;
      return true;
    }
    if (typeof a === 'object' && typeof b === 'object') {
      const ak = Object.keys(a ?? {}), bk = Object.keys(b ?? {});
      if (ak.length !== bk.length) return false;
      for (const k of ak) if (!deepEqual(a[k], b[k])) return false;
      return true;
    }
    return false;
  };
  const getByPath = (obj: any, path: string) =>
    path.split('.').reduce((o, k) => {
      if (o == null) return undefined;
      const idx = /^\d+$/.test(k) ? Number(k) : k;
      return o[idx as any];
    }, obj);
  const isDirty = (path: string) => !deepEqual(getByPath(form, path), getByPath(savedForm, path));

  const dirtySx = (dirty: boolean) =>
    dirty
      ? {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#ef5927',
          borderWidth: '2px',
        },
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'rgba(239, 89, 39, 0.06)',
        },
        '& .MuiSelect-select': {
          backgroundColor: 'rgba(239, 89, 39, 0.06)',
        },
      }
      : undefined;

  useEffect(() => {
    if (!open) return;
    if (!territories?.length) return;
    if (enrichInFlightRef.current) return;

    const idsToFetch = territories
      .filter((t: any) => t?.postal_codes === undefined)
      .map((t: { id: string | number }) => String(t.id))
      .filter((id: string) => !requestedTerritoryIdsRef.current.has(id));

    if (!idsToFetch.length) return;

    idsToFetch.forEach((id: string) =>
      requestedTerritoryIdsRef.current.add(id)
    );

    enrichInFlightRef.current = true;

    (async () => {
      try {
        await enrichTerritoriesWithPostalCodes(idsToFetch);
      } catch (e) {
        console.error('Failed to enrich territories with postal codes', e);
      } finally {
        enrichInFlightRef.current = false;
      }
    })();
  }, [open, territories]);

  useEffect(() => {
    if (!open) return;
    if (initialData) {
      const { id, ...rest } = initialData;
      const patched = {
        ...rest,
        depot_service_time_config: {
          loading_service_time_config: {
            fixed_service_time_s: rest.depot_service_time_config?.loading_service_time_config?.fixed_service_time_s ?? 0,
            variable_service_time_s_per_load:
              rest.depot_service_time_config?.loading_service_time_config?.variable_service_time_s_per_load ?? [0, 0, 0],
          },
          unloading_service_time_config: {
            fixed_service_time_s: rest.depot_service_time_config?.unloading_service_time_config?.fixed_service_time_s ?? 0,
            variable_service_time_s_per_load:
              rest.depot_service_time_config?.unloading_service_time_config?.variable_service_time_s_per_load ?? [0, 0, 0],
          },
        },
      };
      setForm({ ...patched, notes: (rest as any).notes ?? null } as VehicleFormType);
      setSavedForm({ ...patched, notes: (rest as any).notes ?? null } as VehicleFormType);
      setPrimaryTerritoryMode(deriveTerritoryMode(rest.primary_territory_names));
      setSecondaryTerritoryMode(deriveTerritoryMode(rest.secondary_territory_names));
      setUnset({});
    } else {
      setForm(defaultForm);
      setSavedForm(defaultForm);
      setPrimaryTerritoryMode(deriveTerritoryMode(defaultForm.primary_territory_names));
      setSecondaryTerritoryMode(deriveTerritoryMode(defaultForm.secondary_territory_names));
      setUnset({ round_trip: true });
    }
  }, [open, initialData]);

  useEffect(() => {
    if (initialData && loadClasses.length) {
      const match = loadClasses.find(lc => lc.name === initialData.load_class_name);
      if (match) setForm(f => ({ ...f, load_class_name: match.name }));
    }
  }, [initialData, loadClasses]);

  useEffect(() => { if (!hasUnsaved) setUnsavedDismissed(false); }, [hasUnsaved]);

  const handleAttemptClose = () => {
    if (hasUnsaved) {
      setPendingClose(() => () => onClose());
      setConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const handleVarServiceChange = (
    index: number,
    value: number | string,
    phase: 'loading' | 'unloading'
  ) => {
    const n = value === '' ? 0 : Math.max(0, Math.floor(Number(value) || 0)); // int ≥ 0

    const cfg = { ...form.depot_service_time_config };
    const currentArr =
      phase === 'loading'
        ? cfg.loading_service_time_config?.variable_service_time_s_per_load?.length > 0 ? cfg.loading_service_time_config?.variable_service_time_s_per_load : dimensions.map(() => 0)
        : cfg.unloading_service_time_config?.variable_service_time_s_per_load?.length > 0 ? cfg.unloading_service_time_config?.variable_service_time_s_per_load : dimensions.map(() => 0);

    const arr = [...currentArr];
    arr[index] = n;

    if (phase === 'loading') {
      cfg.loading_service_time_config = {
        ...cfg.loading_service_time_config,
        variable_service_time_s_per_load: arr,
      };
    } else {
      cfg.unloading_service_time_config = {
        ...cfg.unloading_service_time_config,
        variable_service_time_s_per_load: arr,
      };
    }
    updateField('depot_service_time_config', cfg);
  };

  const handleStopVarServiceChange = (index: number, value: number | string) => {
    const n = value === '' ? 0 : Math.max(0, Math.floor(Number(value) || 0));

    const cur = form.variable_service_time_s_per_load ?? [0, 0, 0];
    const arr = [...cur];
    arr[index] = n;

    updateField('variable_service_time_s_per_load', arr as any);
  };

  type AnyObj = Record<string, any>;
  const applyUnset = <T extends AnyObj>(draft: T): T => {
    const DELETE_KEY = '__DELETE__';
    const UNSET_TO: Record<string, any> = {
      vehicle_routing_type: null,
      vehicle_type: null,
      length_cm: null,
      width_cm: null,
      height_cm: null,
      max_allowed_distance_km: null,
      max_working_time_min: null,
      travel_time_factor: null,
      operation_end: null,
      road_weight_limit_kg: null,
      max_load_height_cm: null,
      max_load_length_cm: null,
      max_load_width_cm: null,
      load_class_name: null,
      primary_territory_names: [],
      secondary_territory_names: [],
      'operation_start_window.start': null,
      'operation_start_window.end': null,
      'start_location.address.street': null,
      'start_location.address.house_number': null,
      'start_location.address.city': null,
      'start_location.address.postal_code': null,
      'start_location.address.country': null,
      'end_location.address.street': null,
      'end_location.address.house_number': null,
      'end_location.address.city': null,
      'end_location.address.postal_code': null,
      'end_location.address.country': null,
      round_trip: DELETE_KEY,
    };

    const setByPath = (obj: any, path: string, value: any) => {
      const keys = path.split('.');
      let t = obj;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!t[keys[i]]) t[keys[i]] = {};
        t = t[keys[i]];
      }
      t[keys[keys.length - 1]] = value;
    };

    Object.entries(unset).forEach(([path, flagged]) => {
      if (!flagged) return;
      const to = UNSET_TO[path];
      if (to === DELETE_KEY) {
        if (!isEdit) {
          delete (draft as any)[path];
        }
      } else if (typeof to !== 'undefined') {
        setByPath(draft as any, path, to);
      }
    });

    LOCATION_KEYS.forEach(locationKey => {
      const addr = (draft as any)[locationKey]?.address ?? {};
      const emptyAddr = [addr.city, addr.postal_code, addr.country, addr.street, addr.house_number]
        .every((v: any) => v == null || String(v).trim?.() === '');
      if (emptyAddr) (draft as any)[locationKey] = null;
    });

    return draft;
  };

  // undefined = invalid (toast already shown), null = no address entered.
  const cleanLocation = (locationKey: LocationKey) => {
    const address = form[locationKey]?.address ?? {};
    if (isAddressEmpty(address)) return null;

    const city = address.city?.trim();
    const postalCode = address.postal_code?.trim();
    if (!city || !postalCode) {
      toastr.error(i18next.t('common:toast.errorTitle'),
        'City and postal code are required if address is provided.');
      return undefined;
    }
    const addressChanged = ['street', 'house_number', 'city', 'postal_code', 'country']
      .some(k => isDirty(`${locationKey}.address.${k}`));
    return {
      ...form[locationKey],
      address: {
        country: address.country?.trim() || null,
        city,
        postal_code: postalCode,
        street: address.street?.trim() || null,
        house_number: address.house_number?.trim() || null,
      },
      ...(addressChanged ? { coordinates: null } : {}),
    };
  };

  const handleSubmit = async () => {
    setTouched(t => ({ ...t, name: true, capacities: true, max_adr_points: true }));
    if (form.name.trim() === '') return;
    if (form.capacities[0] <= 0) return;

    const cleanedStartLocation = cleanLocation('start_location');
    if (cleanedStartLocation === undefined) return;
    const cleanedEndLocation = cleanLocation('end_location');
    if (cleanedEndLocation === undefined) return;

    const normalizedNotes = (() => {
      const t = (form.notes ?? '').trim();
      const clipped = t.slice(0, NOTES_MAX);
      return clipped.length ? clipped : null;
    })();

    // helper: API errors
    const toErrorText = (detail: any, fallback = 'Unknown error'): string => {
      if (!detail) return fallback;
      if (typeof detail === 'string') return detail;

      // FastAPI: detail = [{type, loc, msg, input}, ...]
      if (Array.isArray(detail)) {
        return detail
          .map((d: any) => {
            if (typeof d === 'string') return d;
            if (d?.loc || d?.msg) {
              const loc = Array.isArray(d.loc) ? d.loc.join('.') : d.loc;
              return loc ? `${loc}: ${d.msg ?? ''}`.trim() : (d.msg ?? JSON.stringify(d));
            }
            try { return JSON.stringify(d); } catch { return String(d); }
          })
          .join('\n');
      }

      if (typeof detail === 'object') {
        if (detail.msg) return detail.msg;
        try { return JSON.stringify(detail); } catch { /* ignore */ }
      }
      return fallback;
    };

    let cleanedForm = applyUnset({
      ...form,
      start_location: cleanedStartLocation,
      end_location: cleanedEndLocation,
      notes: normalizedNotes,
    });
    const dsc = cleanedForm.depot_service_time_config;
    if (dsc?.loading_service_time_config?.fixed_service_time_s == null) {
      delete dsc.loading_service_time_config.fixed_service_time_s;
    }
    if (dsc?.unloading_service_time_config?.fixed_service_time_s == null) {
      delete dsc.unloading_service_time_config.fixed_service_time_s;
    }

    const cfg = cleanedForm.depot_service_time_config;

    if (cfg) {
      const cleanPhase = (phaseCfg: any) => {
        if (!phaseCfg) return phaseCfg;

        return {
          ...phaseCfg,
          fixed_service_time_s: phaseCfg.fixed_service_time_s ?? 0,
          variable_service_time_s_per_load: phaseCfg.variable_service_time_s_per_load ?? [0, 0, 0],
        };
      };

      const newCfg = {
        ...cfg,
        loading_service_time_config: cleanPhase(cfg.loading_service_time_config),
        unloading_service_time_config: cleanPhase(cfg.unloading_service_time_config),
      };

      const hasMeaningfulVar = (arr?: number[]) =>
        Array.isArray(arr) && arr.some(v => (Number(v) || 0) > 0);

      const l = newCfg.loading_service_time_config;
      const u = newCfg.unloading_service_time_config;

      cleanedForm = { ...cleanedForm, depot_service_time_config: newCfg };
    }
    try {
      setSaving(true);
      await onSave(cleanedForm, initialData?.id);
      const successText =
        `${cleanedForm.name || i18next.t('common:vehicleEditor.vehicle')} — ` +
        (isEdit ? i18next.t('common:toast.editSuccess') : i18next.t('common:toast.addSuccess'));

      setSaveToast({
        open: true,
        severity: 'success',
        msg: successText,
      });

      setSavedForm(cleanedForm);
      setUnsavedDismissed(true);
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const text = toErrorText(detail, err?.message || 'Unknown error');
      setSaveToast({
        open: true,
        severity: 'error',
        msg: (
          <Box>
            <Typography sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {i18next.t('common:toast.errorTitle')}
            </Typography>
            <Typography sx={{ mt: 0.2, whiteSpace: 'pre-line' }}>
              {text}
            </Typography>
          </Box>
        ),
      });
    } finally {
      setSaving(false);
    }
  };

  const features: Feature[] = [
    { key: 'has_liftgate', labelKey: 'common:vehicleEditor.liftgate', icon: <WarehouseOutlined /> },
    { key: 'has_trailer', labelKey: 'common:vehicleEditor.trailer', icon: <LocalShippingOutlined /> },
  ];
  const roundTripFeature: RoundTripFeature = {
    key: 'round_trip',
    labelKey: 'common:vehicleEditor.roundtrip',
    icon: <LoopOutlined />,
  };

  const loadingVar =
    form.depot_service_time_config?.loading_service_time_config
      ?.variable_service_time_s_per_load ?? [0, 0, 0];

  const unloadingVar =
    form.depot_service_time_config?.unloading_service_time_config
      ?.variable_service_time_s_per_load ?? [0, 0, 0];



  const blockNonNumericKeys = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const k = e.key.toLowerCase();
    if (k === '-' || k === '+' || k === 'e') e.preventDefault();
  };

  const handlePositiveOrNull =
    <K extends 'length_cm' | 'width_cm' | 'height_cm' | 'max_adr_points'>(key: K, updateField: any) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        if (raw === '') { updateField(key, null); return; }
        const n = Number(raw);
        if (!Number.isFinite(n) || n <= 0) return;
        updateField(key, Math.floor(n));
      };

  const positiveNumberHtmlInputProps = {
    min: 1,
    step: 1,
    inputMode: 'numeric' as const,
    pattern: '[0-9]*',
  };

  const renderAddressSection = (locationKey: LocationKey, title: string) => {
    const address = form[locationKey]?.address ?? {};
    return (
      <>
        <Typography variant="subtitle2" className={classes.addressTitle}>
          {title}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <ClearableTextField
              label={i18next.t('common:vehicleTable.street')}
              size="small"
              fullWidth
              value={address.street ?? ''}
              onChange={e => updateLocation(locationKey, 'address.street', e.target.value || null)}
              InputLabelProps={{ shrink: true }}
              showClear={!!address.street}
              onClear={() => { updateLocation(locationKey, 'address.street', null); markUnset(`${locationKey}.address.street`, true); }}
              sx={dirtySx(isDirty(`${locationKey}.address.street`))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ClearableTextField
              label={i18next.t('common:vehicleTable.houseNumber')}
              size="small"
              fullWidth
              value={address.house_number ?? ''}
              onChange={e => updateLocation(locationKey, 'address.house_number', e.target.value || null)}
              InputLabelProps={{ shrink: true }}
              showClear={!!address.house_number}
              onClear={() => { updateLocation(locationKey, 'address.house_number', null); markUnset(`${locationKey}.address.house_number`, true); }}
              sx={dirtySx(isDirty(`${locationKey}.address.house_number`))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ClearableTextField
              label={i18next.t('common:vehicleTable.city')}
              size="small"
              fullWidth
              value={address.city ?? ''}
              onChange={e => updateLocation(locationKey, 'address.city', e.target.value || null)}
              InputLabelProps={{ shrink: true }}
              showClear={!!address.city}
              onClear={() => { updateLocation(locationKey, 'address.city', null); markUnset(`${locationKey}.address.city`, true); }}
              sx={dirtySx(isDirty(`${locationKey}.address.city`))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ClearableTextField
              label={i18next.t('common:vehicleTable.postalCode')}
              size="small"
              fullWidth
              value={address.postal_code ?? ''}
              onChange={e => updateLocation(locationKey, 'address.postal_code', e.target.value || '')}
              InputLabelProps={{ shrink: true }}
              showClear={!!address.postal_code}
              onClear={() => { updateLocation(locationKey, 'address.postal_code', null); markUnset(`${locationKey}.address.postal_code`, true); }}
              sx={dirtySx(isDirty(`${locationKey}.address.postal_code`))}
            />
          </Grid>
          <Grid item xs={12}>
            <ClearableTextField
              label={i18next.t('common:vehicleTable.country')}
              size="small"
              fullWidth
              value={address.country ?? ''}
              onChange={e => updateLocation(locationKey, 'address.country', e.target.value || null)}
              InputLabelProps={{ shrink: true }}
              showClear={!!address.country}
              onClear={() => { updateLocation(locationKey, 'address.country', null); markUnset(`${locationKey}.address.country`, true); }}
              sx={dirtySx(isDirty(`${locationKey}.address.country`))}
            />
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <Box>
      <Drawer anchor="right" open={open} onClose={handleAttemptClose}>
        <Box className={classes.drawer}>
          <Box className={classes.header}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6" className={classes.addTitle}>
                {isEdit
                  ? i18next.t('common:vehicleEditor.editTitle')
                  : i18next.t('common:vehicleEditor.addTitle')}
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleAttemptClose}><CloseOutlined /></IconButton>
          </Box>

          <Box className={classes.content}>
            {/* Row: Name + Vehicle Type + Vehicle Routing Type */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <ClearableTextField
                  label={i18next.t('common:vehicleTable.name')}
                  size="small"
                  required
                  fullWidth
                  value={form.name}
                  onChange={e => updateField('name', e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, name: true }))}
                  error={!!(touched.name && form.name.trim() === '')}
                  sx={dirtySx(isDirty('name'))}
                  helperText={
                    touched.name && form.name.trim() === ''
                      ? i18next.t('common:validation.requiredField')
                      : undefined
                  }
                  FormHelperTextProps={{ style: { margin: '5px 5px 0px 5px', fontSize: '13px' } }}
                  InputLabelProps={{ classes: { asterisk: classes.redAsterisk }, shrink: true }}
                  showClear={!!form.name}
                  onClear={() => updateField('name', '')}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <ClearableTextField
                  label={i18next.t('common:vehicleTable.vehicleType')}
                  size="small"
                  fullWidth
                  value={form.vehicle_type ?? ''}
                  onChange={e => updateField('vehicle_type', e.target.value || null)}
                  InputLabelProps={{ shrink: true }}
                  showClear={!!form.vehicle_type}
                  onClear={() => { setF('vehicle_type', null); markUnset('vehicle_type', true); }}
                  sx={dirtySx(isDirty('vehicle_type'))}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <ClearableSelect
                  label={i18next.t('common:vehicleTable.vehicleRoutingType')}
                  labelId={vtLabelId}
                  value={form.vehicle_routing_type}
                  onChange={(e) => {
                    const val = (e.target.value ?? '') as any;
                    setF('vehicle_routing_type', val);
                    markUnset('vehicle_routing_type', val === '');
                  }}
                  showClear={false}
                  sx={dirtySx(isDirty('vehicle_routing_type'))}
                >
                  <MenuItem value="" sx={{ display: 'none' }} />
                  {VEHICLE_ROUTING_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>
                  ))}
                </ClearableSelect>
              </Grid>
            </Grid>

            {/* General */}
            <Typography variant="subtitle2" className={classes.headerTitle}>
              {i18next.t('common:vehicleEditor.general')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <ClearableTextField
                  label={i18next.t('common:vehicleTable.lengthInCm')}
                  size="small"
                  type="number"
                  fullWidth
                  value={form.length_cm ?? ''}
                  onChange={handlePositiveOrNull('length_cm', updateField)}
                  onKeyDown={blockNonNumericKeys}
                  InputLabelProps={{ shrink: true }}
                  slotProps={{ htmlInput: positiveNumberHtmlInputProps }}
                  showClear={form.length_cm != null}
                  onClear={() => clearField('length_cm', null)}
                  sx={dirtySx(isDirty('length_cm'))}
                  error={form.length_cm != null && form.length_cm <= 0}
                  helperText={
                    form.length_cm != null && form.length_cm <= 0
                      ? i18next.t('common:validation.mustBeGreaterThanZero')
                      : undefined
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ClearableTextField
                  label={i18next.t('common:vehicleTable.widthInCm')}
                  size="small"
                  type="number"
                  fullWidth
                  value={form.width_cm ?? ''}
                  onChange={handlePositiveOrNull('width_cm', updateField)}
                  onKeyDown={blockNonNumericKeys}
                  InputLabelProps={{ shrink: true }}
                  slotProps={{ htmlInput: positiveNumberHtmlInputProps }}
                  showClear={form.width_cm != null}
                  onClear={() => clearField('width_cm', null)}
                  sx={dirtySx(isDirty('width_cm'))}
                  error={form.width_cm != null && form.width_cm <= 0}
                  helperText={
                    form.width_cm != null && form.width_cm <= 0
                      ? i18next.t('common:validation.mustBeGreaterThanZero')
                      : undefined
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ClearableTextField
                  label={i18next.t('common:vehicleTable.heightInCm')}
                  size="small"
                  type="number"
                  fullWidth
                  value={form.height_cm ?? ''}
                  onChange={handlePositiveOrNull('height_cm', updateField)}
                  onKeyDown={blockNonNumericKeys}
                  InputLabelProps={{ shrink: true }}
                  slotProps={{ htmlInput: positiveNumberHtmlInputProps }}
                  showClear={form.height_cm != null}
                  onClear={() => clearField('height_cm', null)}
                  sx={dirtySx(isDirty('height_cm'))}
                  error={form.height_cm != null && form.height_cm <= 0}
                  helperText={
                    form.height_cm != null && form.height_cm <= 0
                      ? i18next.t('common:validation.mustBeGreaterThanZero')
                      : undefined
                  }
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <ClearableTextField
                  label={i18next.t('common:vehicleTable.maxAdrPoints')}
                  size="small"
                  type="number"
                  fullWidth
                  value={form.max_adr_points}
                  onChange={handlePositiveOrNull('max_adr_points', updateField)}
                  onKeyDown={blockNonNumericKeys}
                  onBlur={() => setTouched(t => ({ ...t, max_adr_points: true }))}

                  InputLabelProps={{
                    shrink: true,
                    classes: { asterisk: classes.redAsterisk }
                  }}

                  slotProps={{ htmlInput: positiveNumberHtmlInputProps }}

                  showClear={(form.max_adr_points ?? 0) !== 0}
                  onClear={() => updateField('max_adr_points', 0 as any)}
                  sx={dirtySx(isDirty('max_adr_points'))}

                  error={form.max_adr_points < 0}
                  helperText={
                    form.max_adr_points < 0
                      ? i18next.t('common:validation.mustBeGreaterThanZero')
                      : undefined
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ClearableTextField
                  label={i18next.t('common:vehicleTable.maxAlloweddistanceKm')}
                  size="small"
                  type="number"
                  fullWidth
                  value={form.max_allowed_distance_km ?? ''}
                  onChange={e => {
                    const value = e.target.value;
                    updateField('max_allowed_distance_km', value === '' ? null : Number(value));
                  }}
                  error={
                    form.max_allowed_distance_km != null &&
                    form.max_allowed_distance_km <= 0
                  }
                  helperText={
                    form.max_allowed_distance_km != null &&
                      form.max_allowed_distance_km <= 0
                      ? i18next.t('common:validation.mustBeGreaterThanZero')
                      : ''
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 1 }}
                  showClear={form.max_allowed_distance_km != null}
                  onClear={() => clearField('max_allowed_distance_km', null)}
                  sx={dirtySx(isDirty('max_allowed_distance_km'))}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                {isEdit ? (
                  <Stack direction="row" flexWrap="wrap" spacing={1}>
                    {(() => {
                      const active = Boolean(form.round_trip);
                      const chipLabel = active
                        ? i18next.t('common:vehicleEditor.withFeature', {
                          feature: i18next.t(roundTripFeature.labelKey),
                        })
                        : i18next.t('common:vehicleEditor.withoutFeature', {
                          feature: i18next.t(roundTripFeature.labelKey),
                        });
                      return (
                        <Chip
                          key={roundTripFeature.key}
                          icon={active ? <CheckCircleOutline fontSize="small" /> : <CancelOutlined fontSize="small" />}
                          label={chipLabel}
                          size="small"
                          clickable
                          color={active ? 'success' : 'error'}
                          variant={active ? 'filled' : 'outlined'}
                          onClick={() => updateField('round_trip', !active)}
                        />
                      );
                    })()}
                  </Stack>
                ) : (
                  <ClearableSelect
                    label={i18next.t(roundTripFeature.labelKey)}
                    value={form.round_trip === null ? '' : String(form.round_trip)}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '') {
                        updateField('round_trip', null);
                        markUnset('round_trip', true);
                      } else {
                        updateField('round_trip', val === 'true');
                        markUnset('round_trip', false);
                      }
                    }}
                    onClear={() => {
                      updateField('round_trip', null);
                      markUnset('round_trip', true);
                    }}
                    showClear={form.round_trip !== null}
                    sx={dirtySx(isDirty('round_trip'))}
                  >
                    <MenuItem value="">
                      <em>{i18next.t('common:vehicleEditor.roundtripStandard')}</em>
                    </MenuItem>
                    <MenuItem value="true">{i18next.t('common:vehicleEditor.roundtripOn')}</MenuItem>
                    <MenuItem value="false">{i18next.t('common:vehicleEditor.roundtripOff')}</MenuItem>
                  </ClearableSelect>
                )}
              </Grid>
            </Grid>

            {/* Capacity */}
            <Typography variant="subtitle2" className={classes.headerTitle}>
              {i18next.t(`common:vehicleEditor.capacityLabel`)}
            </Typography>
            <Grid container spacing={2}>
              {dimensions.map((dim, i) => (
                <Grid item xs={12} sm={4} key={i}>
                  <ClearableTextField
                    label={`Max.${dim?.dimension ?? '-'} ${dim?.unit ?? '-'}`}
                    size="small"
                    type="number"
                    required={i === 0}
                    fullWidth
                    value={form.capacities[i]}
                    onChange={e =>
                      setForm(f => {
                        const c = [...f.capacities];
                        const raw = e.target.value;
                        const n = raw === '' ? 0 : Math.max(0, Number(raw) || 0);
                        c[i] = n;
                        return { ...f, capacities: c };
                      })
                    }
                    onKeyDown={blockNonNumericKeys}
                    inputProps={{ min: 0, step: 1, pattern: '[0-9]*' }}
                    onBlur={() => { if (i === 0) setTouched(t => ({ ...t, capacities: true })); }}
                    error={!!(touched.capacities && i === 0 && form.capacities[0] <= 0)}
                    helperText={
                      touched.capacities && i === 0 && form.capacities[0] <= 0
                        ? i18next.t('common:validation.requiredField')
                        : undefined
                    }
                    FormHelperTextProps={{ sx: { m: 0 } }}
                    InputLabelProps={{ shrink: true, classes: i === 0 ? { asterisk: classes.redAsterisk } : {} }}
                    showClear={i !== 0 && form.capacities[i] !== 0}
                    onClear={() =>
                      setForm(f => {
                        const c = [...f.capacities];
                        c[i] = 0;
                        return { ...f, capacities: c };
                      })
                    }
                    sx={dirtySx(isDirty(`capacities.${i}`))}
                  />
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <ClearableTextField
                  label={i18next.t('common:vehicleEditor.maxLoadHeight')}
                  size="small"
                  type="number"
                  fullWidth
                  value={form.max_load_height_cm ?? ''}
                  onChange={e => {
                    const raw = e.target.value;
                    updateField('max_load_height_cm', raw === '' ? null : Math.max(parseFloat(raw), 0) || null);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 'any', min: 0 }}
                  showClear={form.max_load_height_cm != null}
                  onClear={() => clearField('max_load_height_cm', null)}
                  sx={dirtySx(isDirty('max_load_height_cm'))}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ClearableTextField
                  label={i18next.t('common:vehicleEditor.maxloadLength')}
                  size="small"
                  type="number"
                  fullWidth
                  value={form.max_load_length_cm ?? ''}
                  onChange={e => {
                    const raw = e.target.value;
                    updateField('max_load_length_cm', raw === '' ? null : Math.max(parseFloat(raw), 0) || null);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 'any', min: 0 }}
                  showClear={form.max_load_length_cm != null}
                  onClear={() => clearField('max_load_length_cm', null)}
                  sx={dirtySx(isDirty('max_load_length_cm'))}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ClearableTextField
                  label={i18next.t('common:vehicleEditor.maxloadWidth')}
                  size="small"
                  type="number"
                  fullWidth
                  value={form.max_load_width_cm ?? ''}
                  onChange={e => {
                    const raw = e.target.value;
                    updateField('max_load_width_cm', raw === '' ? null : Math.max(parseFloat(raw), 0) || null);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 'any', min: 0 }}
                  showClear={form.max_load_width_cm != null}
                  onClear={() => clearField('max_load_width_cm', null)}
                  sx={dirtySx(isDirty('max_load_width_cm'))}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <ClearableSelect
                  label={i18next.t('common:vehicleTable.loadClassName')}
                  labelId={lcLabelId}
                  value={form.load_class_name || ''}
                  onChange={e => updateField('load_class_name', e.target.value || null)}
                  onClear={() => { setF('load_class_name', '' as any); markUnset('load_class_name', true); }}
                  showClear={!!form.load_class_name}
                  disabled={loadingClasses}
                  sx={dirtySx(isDirty('load_class_name'))}
                >
                  {loadClasses.map(lc => (
                    <MenuItem key={lc.id} value={lc.name}>{lc.name}</MenuItem>
                  ))}
                </ClearableSelect>
                {loadingClasses && <CircularProgress size={20} sx={{ mt: 1 }} />}
              </Grid>

              <Grid item xs={12} sm={4}>
                <ClearableTextField
                  label={i18next.t('common:vehicleEditor.roadWeightlimit')}
                  size="small"
                  type="number"
                  fullWidth
                  value={form.road_weight_limit_kg ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      updateField('road_weight_limit_kg', null);
                    } else {
                      const n = Number(raw);
                      updateField('road_weight_limit_kg', n > 0 ? Math.floor(n) : null);
                    }
                  }}
                  onKeyDown={blockNonNumericKeys}
                  inputProps={{ min: 1, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                  InputLabelProps={{ shrink: true }}
                  showClear={form.road_weight_limit_kg != null}
                  onClear={() => clearField('road_weight_limit_kg', null)}
                  sx={dirtySx(isDirty('road_weight_limit_kg'))}
                  error={form.road_weight_limit_kg != null && form.road_weight_limit_kg <= 0}
                  helperText={
                    form.road_weight_limit_kg != null && form.road_weight_limit_kg <= 0
                      ? i18next.t('common:validation.mustBeGreaterThanZero')
                      : undefined
                  }
                  FormHelperTextProps={{ sx: { m: 0 } }}
                />
              </Grid>
            </Grid>

            {/* Costs */}
            <Typography variant="subtitle2" className={classes.headerTitle}>
              {i18next.t('common:vehicleEditor.costIn')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <ClearableTextField
                  label={i18next.t('common:vehicleTable.fixedCostEur')}
                  size="small"
                  type="number"
                  fullWidth
                  value={form.fixed_cost_eur}
                  onChange={e => {
                    const raw = e.target.value;
                    const num = raw === '' ? 0 : parseFloat(raw);
                    updateField('fixed_cost_eur', num < 0 ? 0 : num);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0, step: 0.01 }}
                  showClear={(form.fixed_cost_eur ?? 0) !== 0}
                  onClear={() => setF('fixed_cost_eur', 0 as any)}
                  sx={dirtySx(isDirty('fixed_cost_eur'))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ClearableTextField
                  label={i18next.t('common:vehicleTable.variableCost')}
                  size="small"
                  type="number"
                  fullWidth
                  value={form.variable_cost_eur_per_km}
                  onChange={e => {
                    const raw = e.target.value;
                    const num = raw === '' ? 0 : parseFloat(raw);
                    updateField('variable_cost_eur_per_km', num < 0 ? 0 : num);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0, step: 0.01 }}
                  showClear={(form.variable_cost_eur_per_km ?? 0) !== 0}
                  onClear={() => updateField('variable_cost_eur_per_km', 0 as any)}
                  sx={dirtySx(isDirty('variable_cost_eur_per_km'))}
                />
              </Grid>
            </Grid>

            {/* Times */}
            <Typography variant="subtitle2" className={classes.headerTitle}>
              {i18next.t('common:vehicleEditor.TimeTitle')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <ClearableTextField
                  label={i18next.t('common:vehicleTable.traveltime_factor')}
                  size="small"
                  type="number"
                  fullWidth
                  value={form.travel_time_factor ?? ''}
                  onChange={e => {
                    const raw = e.target.value;
                    updateField('travel_time_factor', raw === '' ? null : (parseFloat(raw) > 0 ? parseFloat(raw) : null));
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 'any', min: 0.0001 }}
                  showClear={form.travel_time_factor != null}
                  onClear={() => clearField('travel_time_factor', null)}
                  sx={dirtySx(isDirty('travel_time_factor'))}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <ClearableContainer
                  onClear={() => clearField('operation_end', null)}
                  show={!!form.operation_end}
                  sx={dirtySx(isDirty('operation_end'))}
                >
                  <TimeField
                    label={i18next.t('common:vehicleTable.returnTimeTo')}
                    size="small"
                    valueStr={form.operation_end}
                    onChange={time => updateField('operation_end', time)}
                  />
                </ClearableContainer>
              </Grid>

              <Grid item xs={12} sm={4}>
                <ClearableTextField
                  label={i18next.t('common:vehicleTable.maxWorkingTime')}
                  size="small"
                  type="number"
                  fullWidth
                  value={form.max_working_time_min ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      updateField('max_working_time_min', null);
                    } else {
                      const n = Number(raw);
                      updateField('max_working_time_min', n > 0 ? Math.floor(n) : null);
                    }
                  }}
                  onKeyDown={blockNonNumericKeys}
                  inputProps={{ min: 1, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                  InputLabelProps={{ shrink: true }}
                  showClear={form.max_working_time_min != null}
                  onClear={() => clearField('max_working_time_min', null)}
                  sx={dirtySx(isDirty('max_working_time_min'))}
                  error={form.max_working_time_min != null && form.max_working_time_min <= 0}
                  helperText={
                    form.max_working_time_min != null && form.max_working_time_min <= 0
                      ? i18next.t('common:validation.mustBeGreaterThanZero')
                      : undefined
                  }
                  FormHelperTextProps={{ sx: { m: 0 } }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <ClearableContainer
                  onClear={() => { updateField('operation_start_window', { ...form.operation_start_window, start: null }); markUnset('operation_start_window.start', true); }}
                  show={!!form.operation_start_window?.start}
                  sx={dirtySx(isDirty('operation_start_window.start'))}
                >
                  <TimeField
                    label={i18next.t('common:vehicleTable.departureWindowStart')}
                    size="small"
                    valueStr={form.operation_start_window?.start}
                    onChange={time => { updateField('operation_start_window', { ...form.operation_start_window, start: time }); markUnset('operation_start_window.start', false); }}
                  />
                </ClearableContainer>
              </Grid>

              <Grid item xs={12} sm={6}>
                <ClearableContainer
                  onClear={() => { updateField('operation_start_window', { ...form.operation_start_window, end: null }); markUnset('operation_start_window.end', true); }}
                  show={!!form.operation_start_window?.end}
                  sx={dirtySx(isDirty('operation_start_window.end'))}
                >
                  <TimeField
                    label={i18next.t('common:vehicleTable.departureWindowEnd')}
                    size="small"
                    valueStr={form.operation_start_window?.end}
                    onChange={time => { updateField('operation_start_window', { ...form.operation_start_window, end: time }); markUnset('operation_start_window.end', false); }}
                  />
                </ClearableContainer>
              </Grid>
            </Grid>

            {/* Stops service time (all stops) */}
            <Typography variant="subtitle2" className={classes.headerTitle}>
              {i18next.t('common:vehicleTable.stopServiceTime')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <ClearableTextField
                  label={i18next.t('common:vehicleTable.stopFixedServiceTime')}
                  size="small"
                  type="number"
                  fullWidth
                  value={form.fixed_service_time_s === 0 ? '' : (form.fixed_service_time_s ?? '')}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const n = raw === '' ? 0 : Math.max(0, Math.floor(Number(raw) || 0));
                    updateField('fixed_service_time_s', n as any);
                  }}
                  onKeyDown={blockNonNumericKeys}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0, step: 1 }}
                  showClear={(form.fixed_service_time_s ?? 0) !== 0}
                  onClear={() => updateField('fixed_service_time_s', 0 as any)}
                  sx={dirtySx(isDirty('fixed_service_time_s'))}
                />
              </Grid>
              {dimensions.map((dim, i) => (
                <Grid item xs={12} sm={4} key={`stop-var-${i}`}>
                  <ClearableTextField
                    label={i18next.t('common:vehicleTable.stopServiceLabel', { n: i + 1 })}
                    size="small"
                    type="number"
                    fullWidth
                    value={stopVar[i] === 0 ? '' : stopVar[i]}
                    inputProps={{ min: 0, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const n = raw === '' ? 0 : Math.max(0, Math.floor(Number(raw) || 0));
                      handleStopVarServiceChange(i, n);
                    }}
                    onKeyDown={blockNonNumericKeys}
                    InputLabelProps={{ shrink: true }}
                    showClear={stopVar[i] !== 0}
                    onClear={() => handleStopVarServiceChange(i, 0)}
                    sx={dirtySx(isDirty(`variable_service_time_s_per_load.${i}`))}
                    FormHelperTextProps={{ sx: { m: 0 } }}
                  />
                </Grid>
              ))}


            </Grid>

            {/* Load/Unload service times */}
            <Typography variant="subtitle2" className={classes.headerTitle}>
              {i18next.t('common:vehicleTable.loadServiceTime')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <ClearableTextField
                  label={i18next.t('common:vehicleTable.loadingFixedServiceTime')}
                  size="small"
                  type="number"
                  fullWidth
                  value={
                    form.depot_service_time_config
                      ?.loading_service_time_config?.fixed_service_time_s ?? ''
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    const n = raw === '' ? null : Math.max(0, Math.floor(Number(raw) || 0));

                    updateField('depot_service_time_config', {
                      ...form.depot_service_time_config,
                      loading_service_time_config: {
                        ...form.depot_service_time_config.loading_service_time_config,
                        fixed_service_time_s: n,
                      },
                    });
                  }}
                  onKeyDown={blockNonNumericKeys}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0, step: 1 }}
                  showClear={
                    form.depot_service_time_config
                      ?.loading_service_time_config?.fixed_service_time_s != null
                  }
                  onClear={() =>
                    updateField('depot_service_time_config', {
                      ...form.depot_service_time_config,
                      loading_service_time_config: {
                        ...form.depot_service_time_config.loading_service_time_config,
                        fixed_service_time_s: null,
                      },
                    })
                  }
                />
              </Grid>
              {dimensions.map((dim, i) => (
                <Grid item xs={12} sm={4} key={`load-${i}`}>
                  <ClearableTextField
                    label={i18next.t(`common:vehicleTable.loadingServiceLabel${i + 1}`)}
                    size="small"
                    type="number"
                    fullWidth
                    value={loadingVar[i] === 0 ? '' : loadingVar[i]}
                    inputProps={{ min: 0, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const n = raw === '' ? 0 : Math.max(0, Math.floor(Number(raw) || 0));
                      handleVarServiceChange(i, n, 'loading');
                    }}
                    onKeyDown={blockNonNumericKeys}
                    InputLabelProps={{ shrink: true }}
                    showClear={loadingVar[i] !== 0}
                    onClear={() => handleVarServiceChange(i, 0, 'loading')}
                    sx={dirtySx(
                      isDirty(
                        `depot_service_time_config.loading_service_time_config.variable_service_time_s_per_load.${i}`
                      )
                    )}
                    FormHelperTextProps={{ sx: { m: 0 } }}
                  />
                </Grid>
              ))}

            </Grid>

            <Typography variant="subtitle2" className={classes.headerTitle}>
              {i18next.t('common:vehicleTable.unloadServiceTime')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <ClearableTextField
                  label={i18next.t('common:vehicleTable.unloadingFixedServiceTime')}
                  size="small"
                  type="number"
                  fullWidth
                  value={
                    form.depot_service_time_config
                      ?.unloading_service_time_config?.fixed_service_time_s ?? ''
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    const n = raw === '' ? null : Math.max(0, Math.floor(Number(raw) || 0));

                    updateField('depot_service_time_config', {
                      ...form.depot_service_time_config,
                      unloading_service_time_config: {
                        ...form.depot_service_time_config.unloading_service_time_config,
                        fixed_service_time_s: n,
                      },
                    });
                  }}
                  onKeyDown={blockNonNumericKeys}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0, step: 1 }}
                  showClear={
                    form.depot_service_time_config
                      ?.unloading_service_time_config?.fixed_service_time_s != null
                  }
                  onClear={() =>
                    updateField('depot_service_time_config', {
                      ...form.depot_service_time_config,
                      unloading_service_time_config: {
                        ...form.depot_service_time_config.unloading_service_time_config,
                        fixed_service_time_s: null,
                      },
                    })
                  }
                />
              </Grid>
              {dimensions.map((dim, i) => (
                <Grid item xs={12} sm={4} key={`unload-${i}`}>
                  <ClearableTextField
                    label={i18next.t(`common:vehicleTable.unloadingServiceLabel${i + 1}`)}
                    size="small"
                    type="number"
                    fullWidth
                    value={unloadingVar[i] === 0 ? '' : unloadingVar[i]}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const n = raw === '' ? 0 : Math.max(0, Math.floor(Number(raw) || 0));
                      handleVarServiceChange(i, n, 'unloading');
                    }}
                    onKeyDown={blockNonNumericKeys}
                    inputProps={{
                      min: 0,
                      step: 1,
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                    }}
                    InputLabelProps={{ shrink: true }}
                    showClear={unloadingVar[i] !== 0}
                    onClear={() => handleVarServiceChange(i, 0, 'unloading')}
                    sx={dirtySx(
                      isDirty(
                        `depot_service_time_config.unloading_service_time_config.variable_service_time_s_per_load.${i}`
                      )
                    )}
                    FormHelperTextProps={{ sx: { m: 0 } }}
                  />
                </Grid>
              ))}

            </Grid>

            {/* Equipment */}
            <Typography variant="subtitle2" className={classes.headerTitle}>
              {i18next.t('common:vehicleEditor.Equipment')}
            </Typography>
            <Stack direction="row" flexWrap="wrap" spacing={1}>
              {([{ key: 'has_liftgate', labelKey: 'common:vehicleEditor.liftgate', icon: <WarehouseOutlined /> },
              { key: 'has_trailer', labelKey: 'common:vehicleEditor.trailer', icon: <LocalShippingOutlined /> }] as Feature[])
                .map(f => {
                  const active = form[f.key];
                  const chipLabel = active
                    ? i18next.t('common:vehicleEditor.withFeature', { feature: i18next.t(f.labelKey) })
                    : i18next.t('common:vehicleEditor.withoutFeature', { feature: i18next.t(f.labelKey) });
                  return (
                    <Chip
                      key={f.key}
                      icon={active ? <CheckCircleOutline fontSize="small" /> : <CancelOutlined fontSize="small" />}
                      label={chipLabel}
                      size="small"
                      clickable
                      color={active ? 'success' : 'error'}
                      variant={active ? 'filled' : 'outlined'}
                      onClick={() => updateField(f.key, !active)}
                    />
                  );
                })}
            </Stack>

            {/* Territories */}
            <Typography variant="subtitle2" className={classes.headerTitle}>
              {i18next.t('common:vehicleTable.Territory')}
            </Typography>
            {(() => {
              const getKey = (t: any) => (t?.code ?? t?.name ?? t?.id ?? '').toString();

              const primarySelected = form.primary_territory_names ?? [];
              const secondarySelected = form.secondary_territory_names ?? [];

              const primaryOptions = (territories ?? []).filter((t: any) => !secondarySelected.includes(getKey(t)));
              const secondaryOptions = (territories ?? []).filter((t: any) => !primarySelected.includes(getKey(t)));

              const handleRadioChange = (
                field: 'primary_territory_names' | 'secondary_territory_names',
                value: string,
              ) => {
                const setMode = field === 'primary_territory_names' ? setPrimaryTerritoryMode : setSecondaryTerritoryMode;
                setMode(value as TerritoryMode);
                if (value === 'all') updateField(field, null as any);
                else if (value === 'none') updateField(field, [] as any);
                else updateField(field, (form[field] ?? []) as any); // keep current selection
              };

              const handleCheckboxToggle = (
                field: 'primary_territory_names' | 'secondary_territory_names',
                key: string,
              ) => {
                const current = form[field] ?? [];
                const next = current.includes(key)
                  ? current.filter((k: string) => k !== key)
                  : [...current, key];
                updateField(field, next as any);
              };

              const renderTerritoryGroup = (
                label: string,
                field: 'primary_territory_names' | 'secondary_territory_names',
                radioValue: 'all' | 'selected' | 'none',
                selected: string[],
                options: any[],
              ) => (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: isDirty(field) ? '#ef5927' : 'divider',
                    borderWidth: isDirty(field) ? '2px' : '1px',
                    borderRadius: 1,
                    p: 1.5,
                    backgroundColor: isDirty(field) ? 'rgba(239, 89, 39, 0.06)' : undefined,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {label}
                  </Typography>
                  <RadioGroup
                    value={radioValue}
                    onChange={(e) => handleRadioChange(field, e.target.value)}
                  >
                    <FormControlLabel
                      value="all"
                      control={<Radio size="small" />}
                      label={i18next.t('common:vehicleTable.allTerritoriesAllowed')}
                      disabled={loadingClasses || loading}
                    />
                    <FormControlLabel
                      value="selected"
                      control={<Radio size="small" />}
                      label={i18next.t('common:vehicleTable.onlySelectedTerritories')}
                      disabled={loadingClasses || loading}
                    />
                    {radioValue === 'selected' && (
                      <Box
                        sx={{
                          ml: 4,
                          maxHeight: 200,
                          overflowY: 'auto',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 0.5,
                          mb: 0.5,
                        }}
                      >
                        {options.map((t: any) => {
                          const key = getKey(t);
                          const checked = selected.includes(key);
                          const postalCount = Array.isArray(t?.postal_codes) ? t.postal_codes.length : 0;
                          return (
                            <FormControlLabel
                              key={t.id ?? key}
                              sx={{ display: 'flex', ml: 0, mr: 0 }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={checked}
                                  onChange={() => handleCheckboxToggle(field, key)}
                                  disabled={loadingClasses || loading}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2">{key}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {i18next.t('common:vehicleTable.postalCodesCount', { count: postalCount })}
                                  </Typography>
                                </Box>
                              }
                            />
                          );
                        })}
                      </Box>
                    )}
                    <FormControlLabel
                      value="none"
                      control={<Radio size="small" />}
                      label={i18next.t('common:vehicleTable.noTerritoriesAllowed')}
                      disabled={loadingClasses || loading}
                    />
                  </RadioGroup>
                </Box>
              );

              return (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    {renderTerritoryGroup(
                      i18next.t('common:vehicleTable.primaryTerritories'),
                      'primary_territory_names',
                      primaryTerritoryMode,
                      primarySelected,
                      primaryOptions,
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {renderTerritoryGroup(
                      i18next.t('common:vehicleTable.secondaryTerritories'),
                      'secondary_territory_names',
                      secondaryTerritoryMode,
                      secondarySelected,
                      secondaryOptions,
                    )}
                  </Grid>
                </Grid>
              );
            })()}

            {renderAddressSection('start_location', i18next.t('common:vehicleEditor.StartAddress'))}
            {renderAddressSection('end_location', i18next.t('common:vehicleEditor.EndAddress'))}

            {/* Notes */}
            <Typography variant="subtitle2" className={classes.headerTitle}>
              {i18next.t('common:vehicleEditor.notesTitle')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ClearableTextField
                  label={i18next.t('common:vehicleEditor.notesLabel') || 'Additional Notes'}
                  multiline
                  minRows={4}
                  maxRows={12}
                  placeholder={i18next.t('common:vehicleEditor.notesLabel') || 'Write notes...'}
                  fullWidth
                  size="small"
                  value={form.notes ?? ''}
                  onChange={(e) => {
                    const v = (e.target.value || '').slice(0, NOTES_MAX);
                    updateField('notes', v);
                  }}
                  inputProps={{ maxLength: NOTES_MAX }}
                  InputLabelProps={{ shrink: true }}
                  showClear={!!(form.notes && form.notes.length)}
                  onClear={() => updateField('notes', '')}
                  sx={dirtySx(isDirty('notes'))}
                />
                <Box display="flex" justifyContent="flex-end" mt={0.5}>
                  <Typography variant="caption">
                    {(form.notes?.length ?? 0)} / {NOTES_MAX} characters
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box display="flex" justifyContent="flex-end" p={1} gap={2}>
            <Button variant="contained" onClick={handleSubmit}>
              {i18next.t('common:settings.save')}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Unsaved changes Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 1, pb: 0 }}>
          <div className={classes.warningwrapper}>
            <WarningAmberOutlined className={classes.warningIcon} sx={{ mr: 2 }} />
          </div>
          <Typography className={classes.warningTitle} variant="subtitle2" id="warning-dialog-title">
            {i18next.t('common:vehicleEditor.unsavedChangesDialogTitle')}
          </Typography>
          <IconButton
            size="small"
            sx={{ position: 'absolute', right: 8, top: 8 }}
            onClick={() => {
              setForm(savedForm);
              setConfirmOpen(false);
              pendingClose();
            }}
          >
            <CloseOutlined />
          </IconButton>
        </DialogTitle>

        <DialogContent className={classes.contentArea}>
          <DialogContentText>
            {i18next.t('common:vehicleEditor.unsavedChangesDialogContent')}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            className={classes.Closebtn}
            onClick={() => {
              setForm(savedForm);
              setConfirmOpen(false);
              pendingClose();
            }}
          >
            {i18next.t('common:buttons.close')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              handleSubmit();
              setSavedForm(form);
              setConfirmOpen(false);
              pendingClose();
            }}
          >
            {i18next.t('common:settings.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved changes Snackbar */}
      <CustomToast
        open={hasUnsaved && !unsavedDismissed}
        message={i18next.t('common:vehicleEditor.unsavedChangesDialogTitle')}
        severity="warning"
        autoHideMs={false}
        anchor={{ vertical: 'bottom', horizontal: 'center' }}
        onSave={handleSubmit}
        saveLabel={i18next.t('common:settings.save')}
        saveDisabled={saving}
        onClose={() => setUnsavedDismissed(true)}
      />

      {/* Save result toast */}
      <CustomToast
        open={saveToast.open}
        message={saveToast.msg}
        severity={saveToast.severity}
        autoHideMs={2500}
        anchor={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setSaveToast(s => ({ ...s, open: false }))}
      />
    </Box>
  );
};

export default VehicleFormDrawer;
